import React, { useState, useEffect } from 'react';
import { Upload, FileText, Image, Palette, Type, CheckCircle2, X, Loader2, Shield, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { supabaseHelpers } from '../lib/supabase';
import { withBackend } from '../utils/api';

export function BrandGuidelinesUpload({ userId }) {
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedGuidelines, setSavedGuidelines] = useState(null);
  const [files, setFiles] = useState({
    document: null,
    logo: null,
  });
  const [brandData, setBrandData] = useState({
    colors: [],
    fonts: [],
    spacing: '',
    logoRules: ''
  });

  // Load existing brand guidelines on mount
  useEffect(() => {
    if (userId) {
      loadExistingGuidelines();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const loadExistingGuidelines = async () => {
    try {
      const { data, error } = await supabaseHelpers.getBrandGuidelines(userId);
      if (!error && data) {
        setSavedGuidelines(data);
        // Pre-populate form with existing data
        setBrandData({
          colors: data.colors || [],
          fonts: data.fonts || [],
          spacing: data.spacing || '',
          logoRules: data.logoRules || ''
        });
      }
    } catch (error) {
      console.error('Failed to load brand guidelines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (type, file) => {
    if (file) {
      setFiles(prev => ({ ...prev, [type]: file }));
      toast.success(`${type === 'document' ? 'Document' : 'Logo'} selected: ${file.name}`);
      
      // Auto-extract brand info from PDF documents
      if (type === 'document' && file.name.toLowerCase().endsWith('.pdf')) {
        await extractBrandFromPdf(file);
      }
    }
  };

  const extractBrandFromPdf = async (file) => {
    setExtracting(true);
    toast.loading('Extracting brand info from PDF...', { id: 'extract-brand' });
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(withBackend('/extract-brand-from-pdf'), {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to extract brand info');
      }
      
      const data = await response.json();
      
      if (data.success && data.extracted) {
        const extracted = data.extracted;
        
        // Update brand data with extracted info
        setBrandData(prev => ({
          colors: extracted.colors || prev.colors,
          fonts: extracted.fonts || prev.fonts,
          spacing: extracted.spacing || prev.spacing,
          logoRules: extracted.logoRules || prev.logoRules
        }));
        
        const colorCount = extracted.colors?.length || 0;
        const fontCount = extracted.fonts?.length || 0;
        toast.success(
          `Extracted ${colorCount} colors and ${fontCount} fonts from PDF!`,
          { id: 'extract-brand' }
        );
      }
    } catch (error) {
      console.error('PDF extraction error:', error);
      toast.error('Failed to extract brand info from PDF', { id: 'extract-brand' });
    } finally {
      setExtracting(false);
    }
  };

  const handleRemoveFile = (type) => {
    setFiles(prev => ({ ...prev, [type]: null }));
  };

  const handleColorAdd = () => {
    const colorInput = document.getElementById('color-input').value;
    if (colorInput && /^#[0-9A-F]{6}$/i.test(colorInput)) {
      setBrandData(prev => ({
        ...prev,
        colors: [...prev.colors, { hex: colorInput, name: '' }]
      }));
      document.getElementById('color-input').value = '';
    } else {
      toast.error('Please enter a valid hex color (e.g., #FF5733)');
    }
  };

  const handleColorRemove = (index) => {
    setBrandData(prev => ({
      ...prev,
      colors: prev.colors.filter((_, i) => i !== index)
    }));
  };

  const handleFontAdd = () => {
    const fontInput = document.getElementById('font-input').value;
    if (fontInput.trim()) {
      setBrandData(prev => ({
        ...prev,
        fonts: [...prev.fonts, fontInput.trim()]
      }));
      document.getElementById('font-input').value = '';
    }
  };

  const handleFontRemove = (index) => {
    setBrandData(prev => ({
      ...prev,
      fonts: prev.fonts.filter((_, i) => i !== index)
    }));
  };

  const handleUpload = async () => {
    if (!userId) {
      toast.error('Please log in to save brand guidelines');
      return;
    }

    setUploading(true);

    try {
      let logoUrl = savedGuidelines?.logoUrl || null;
      let documentUrl = savedGuidelines?.documentUrl || null;

      // Upload logo to Supabase Storage if provided
      if (files.logo) {
        const { data, error } = await supabaseHelpers.uploadBrandAsset(userId, files.logo, 'logo');
        if (error) {
          console.error('Logo upload error:', error);
          toast.error('Failed to upload logo');
        } else {
          logoUrl = data.url;
        }
      }

      // Upload document to Supabase Storage if provided
      if (files.document) {
        const { data, error } = await supabaseHelpers.uploadBrandAsset(userId, files.document, 'guidelines');
        if (error) {
          console.error('Document upload error:', error);
          toast.error('Failed to upload document');
        } else {
          documentUrl = data.url;
        }
      }

      // Save brand guidelines to Supabase database
      const guidelinesData = {
        colors: brandData.colors,
        fonts: brandData.fonts,
        spacing: brandData.spacing,
        logoRules: brandData.logoRules,
        logoUrl,
        documentUrl,
        updatedAt: new Date().toISOString()
      };

      const { data, error } = await supabaseHelpers.saveBrandGuidelines(userId, guidelinesData);

      if (error) {
        throw new Error('Failed to save brand guidelines');
      }

      setSavedGuidelines(guidelinesData);
      toast.success('Brand guidelines saved successfully!');

      // Clear file inputs (keep brand data populated)
      setFiles({ document: null, logo: null });

    } catch (error) {
      console.error('Error saving brand guidelines:', error);
      toast.error('Failed to save brand guidelines');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        <span className="ml-3 text-muted-foreground">Loading brand guidelines...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Saved Guidelines Banner */}
      {savedGuidelines && (
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Brand Guidelines Saved</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Your brand guidelines are saved and will be used for the Create PPT feature.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {savedGuidelines.colors?.length > 0 && (
                      <Badge variant="outline" className="border-green-300">
                        {savedGuidelines.colors.length} colors
                      </Badge>
                    )}
                    {savedGuidelines.fonts?.length > 0 && (
                      <Badge variant="outline" className="border-green-300">
                        {savedGuidelines.fonts.length} fonts
                      </Badge>
                    )}
                    {savedGuidelines.logoUrl && (
                      <Badge variant="outline" className="border-green-300">Logo uploaded</Badge>
                    )}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={loadExistingGuidelines}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Banner */}
      <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-indigo-200 dark:border-indigo-800">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {savedGuidelines ? 'Update Your Brand Guidelines' : 'Upload Your Brand Guidelines'}
              </h3>
              <p className="text-sm text-muted-foreground">
                Upload your brand guideline documents, logos, and specify brand colors and fonts.
                This information will be used for the Create PPT feature and brand compliance checks.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* File Uploads */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document Upload
            </CardTitle>
            <CardDescription>Upload brand guideline documents (PDF, PPTX)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Document Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Brand Guidelines Document</label>
              {!files.document ? (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-accent/5 transition-all">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload PDF or PPTX</p>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.pptx,.ppt"
                    onChange={(e) => handleFileSelect('document', e.target.files[0])}
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg border border-accent/20">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-accent" />
                    <span className="text-sm font-medium">{files.document.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFile('document')}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Logo Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Logo Files</label>
              {!files.logo ? (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-accent/5 transition-all">
                  <Image className="w-8 h-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload PNG, SVG, or JPG</p>
                  <input
                    type="file"
                    className="hidden"
                    accept=".png,.svg,.jpg,.jpeg"
                    onChange={(e) => handleFileSelect('logo', e.target.files[0])}
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg border border-accent/20">
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-accent" />
                    <span className="text-sm font-medium">{files.logo.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFile('logo')}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Brand Specifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Brand Specifications
            </CardTitle>
            <CardDescription>Define your brand colors and fonts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Colors */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Brand Colors</label>
              <div className="flex gap-2">
                <Input
                  id="color-input"
                  placeholder="#FF5733"
                  className="flex-1"
                />
                <Button onClick={handleColorAdd} size="sm">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {brandData.colors.map((color, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1 bg-background rounded-lg border border-border"
                  >
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="text-xs font-mono">{color.hex}</span>
                    <button
                      onClick={() => handleColorRemove(index)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Fonts */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Brand Fonts</label>
              <div className="flex gap-2">
                <Input
                  id="font-input"
                  placeholder="e.g., Helvetica, Arial"
                  className="flex-1"
                />
                <Button onClick={handleFontAdd} size="sm">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {brandData.fonts.map((font, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-2">
                    <Type className="w-3 h-3" />
                    {font}
                    <button
                      onClick={() => handleFontRemove(index)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Specifications */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Guidelines</CardTitle>
          <CardDescription>Optional: Add specific spacing, margin, or logo usage rules</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Spacing & Margin Rules</label>
            <Input
              placeholder="e.g., Minimum 0.5 inch margins on all sides"
              value={brandData.spacing}
              onChange={(e) => setBrandData(prev => ({ ...prev, spacing: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Logo Usage Rules</label>
            <Input
              placeholder="e.g., Logo must appear on bottom-right of every slide"
              value={brandData.logoRules}
              onChange={(e) => setBrandData(prev => ({ ...prev, logoRules: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Upload Button */}
      <div className="flex justify-end gap-4">
        <Button
          variant="outline"
          onClick={() => {
            setFiles({ document: null, logo: null });
            setBrandData({ colors: [], fonts: [], spacing: '', logoRules: '' });
          }}
        >
          Clear All
        </Button>
        <Button
          onClick={handleUpload}
          disabled={uploading || (!files.document && !files.logo && brandData.colors.length === 0 && brandData.fonts.length === 0)}
          className="bg-gradient-to-r from-accent to-accent/90"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {savedGuidelines ? 'Update Brand Guidelines' : 'Save Brand Guidelines'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
